import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, X, Save, Trash2, UserCog } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TechniciansPage() {
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({});

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const fetchTechs = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/technicians`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) setTechs(d.technicians);
    } catch { toast.error('Failed to load technicians'); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTechs(); }, [fetchTechs]);

  const emptyForm = { name: '', hourly_rate: '', email: '', phone: '', role: '' };
  const startCreate = () => { setCreating(true); setEditing(null); setForm(emptyForm); };
  const startEdit = (t) => { setEditing(t.id); setCreating(false); setForm({ ...t, hourly_rate: t.hourly_rate ?? '' }); };
  const cancel = () => { setEditing(null); setCreating(false); };

  const handleSave = async () => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    try {
      const body = { ...form, hourly_rate: form.hourly_rate === '' ? 0 : Number(form.hourly_rate) || 0 };
      const url = editing ? `${API}/api/technicians/${editing}` : `${API}/api/technicians`;
      const method = editing ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!r.ok) { toast.error('Save failed'); return; }
      toast.success(editing ? 'Technician updated' : 'Technician added');
      cancel();
      fetchTechs();
    } catch { toast.error('Save failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await fetch(`${API}/api/technicians/${id}`, { method: 'DELETE', headers });
      toast.success('Deleted');
      fetchTechs();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="technicians-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">{techs.length} technician{techs.length !== 1 ? 's' : ''}</div>
        <Button data-testid="add-tech-btn" onClick={startCreate} size="sm"><Plus className="h-4 w-4 mr-1" />Add Technician</Button>
      </div>

      {/* Create form */}
      {creating && (
        <div className="bg-card border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">New Technician</h2>
            <Button variant="ghost" size="icon" onClick={cancel}><X className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div><label className="text-xs text-muted-foreground mb-1 block">Name *</label><Input data-testid="input-tech-name" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Hourly Rate</label><Input type="number" step="0.01" value={form.hourly_rate ?? ''} onChange={e => setForm(p => ({ ...p, hourly_rate: e.target.value }))} placeholder="$/hr" /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Role</label><Input value={form.role || ''} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} placeholder="Mechanic" /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Email</label><Input value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
            <div><label className="text-xs text-muted-foreground mb-1 block">Phone</label><Input value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} /></div>
          </div>
          <div className="flex gap-2">
            <Button data-testid="save-tech-btn" onClick={handleSave} size="sm"><Save className="h-4 w-4 mr-1" />Save</Button>
            <Button variant="outline" size="sm" onClick={cancel}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? <div className="text-center py-8 text-muted-foreground">Loading...</div> : (
        <div className="border rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/30 border-b">
                <th className="text-left p-3 font-medium">Name</th>
                <th className="text-right p-3 font-medium">Rate ($/hr)</th>
                <th className="text-left p-3 font-medium">Role</th>
                <th className="text-left p-3 font-medium hidden sm:table-cell">Email</th>
                <th className="text-left p-3 font-medium hidden sm:table-cell">Phone</th>
                <th className="p-3 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {techs.map((t, i) => editing === t.id ? (
                <tr key={t.id} className="border-b bg-muted/20">
                  <td colSpan={6} className="p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
                      <div><label className="text-xs text-muted-foreground block">Name *</label><Input className="h-8 text-xs" value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
                      <div><label className="text-xs text-muted-foreground block">Rate</label><Input className="h-8 text-xs" type="number" step="0.01" value={form.hourly_rate ?? ''} onChange={e => setForm(p => ({ ...p, hourly_rate: e.target.value }))} /></div>
                      <div><label className="text-xs text-muted-foreground block">Role</label><Input className="h-8 text-xs" value={form.role || ''} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} /></div>
                      <div><label className="text-xs text-muted-foreground block">Email</label><Input className="h-8 text-xs" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
                      <div><label className="text-xs text-muted-foreground block">Phone</label><Input className="h-8 text-xs" value={form.phone || ''} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} /></div>
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleSave} size="sm" className="h-7 text-xs"><Save className="h-3.5 w-3.5 mr-1" />Save</Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={cancel}>Cancel</Button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={t.id} data-testid={`tech-row-${i}`} className="border-b last:border-0 hover:bg-muted/10 transition-colors">
                  <td className="p-3 font-bold">{t.name}</td>
                  <td className="p-3 text-right font-mono">{t.hourly_rate ? `$${Number(t.hourly_rate).toFixed(2)}` : '—'}</td>
                  <td className="p-3 text-muted-foreground">{t.role || '—'}</td>
                  <td className="p-3 text-muted-foreground text-xs hidden sm:table-cell">{t.email || '—'}</td>
                  <td className="p-3 text-muted-foreground text-xs hidden sm:table-cell">{t.phone || '—'}</td>
                  <td className="p-3">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(t)} data-testid={`edit-tech-${i}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-300" onClick={() => handleDelete(t.id, t.name)} data-testid={`delete-tech-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {techs.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No technicians yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
